import React from "react";
import { useCategories } from "./context/CategoriesContext";
import { useNavigate } from "react-router-dom";

const FALLBACK_IMAGE =
  "";

const ShopByCategory = () => {
  const { categories, loading } = useCategories();
  const navigate = useNavigate();

  const handleViewAll = () => {
    navigate("/products");
  };

  const handleCategoryClick = (category) => {
    navigate(`/products#category-${category.id}`);
  };

  // Only active categories
  const activeCategories = categories.filter(
    (c) => c.status === "Active"
  );

  // Featured + grid split
  const featuredCategory = activeCategories[0];
  const smallCategories = activeCategories.slice(1, 7);

  if (loading) return null;
  if (!activeCategories.length) return null;

  return (
    <section
      className="flex flex-col items-start px-4 sm:px-5 md:px-6 py-8 sm:py-10 md:py-11 mt-10 sm:mt-10 md:mt-10 lg:mt-10 w-full bg-black rounded-2xl sm:rounded-[30px]"
      role="region"
      aria-labelledby="shop-by-category-heading"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start sm:items-center w-full">
        <h2
          id="shop-by-category-heading"
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-wide font-semibold leading-tight text-white"
          style={{ fontFamily: "Gloock, serif" }}
        >
          Shop by Category
        </h2>

        <button
          onClick={handleViewAll}
          className="px-4 py-2 bg-white rounded shadow-md text-sm sm:text-base text-neutral-800 hover:bg-gray-100 transition sm:block hidden"
        >
          View All
        </button>
      </div>

      <p className="mt-2 text-xs sm:text-sm font-medium leading-loose text-white uppercase">
        Find the perfect product to customize
      </p>

      {/* Mobile View All Button - Centered */}
      <div className="w-full flex justify-center mt-4 sm:hidden">
        <button
          onClick={handleViewAll}
          className="px-4 py-2 bg-white rounded shadow-md text-sm text-neutral-800 hover:bg-gray-100 transition"
        >
          View All
        </button>
      </div>

      {/* Layout */}
      <div className="self-stretch mt-6">
        <div className="flex flex-col md:flex-col lg:flex-row gap-4 lg:gap-5">

          {/* Featured Category */}
          {featuredCategory && (
            <div className="w-full lg:w-[40%]">
              <article
                className="relative flex flex-col justify-end h-[280px] sm:h-[320px] md:h-[360px] lg:h-full bg-white rounded-lg overflow-hidden cursor-pointer group transition hover:scale-[1.02]"
                onClick={() => handleCategoryClick(featuredCategory)}
              >
                <img
                  src={featuredCategory.image || FALLBACK_IMAGE}
                  alt={featuredCategory.title}
                  className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition"
                />

                <div className="relative bg-[#F5F5DC] p-4 sm:p-6 rounded-t-xl text-center">
                  <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold uppercase group-hover:text-teal-600 transition">
                    {featuredCategory.title}
                  </h3>

                  <button
                    className="mt-4 px-5 py-2 text-sm sm:text-base text-white bg-black rounded-3xl hover:bg-gray-800 transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryClick(featuredCategory);
                    }}
                  >
                    Explore Category
                  </button>
                </div>
              </article>
            </div>
          )}

          {/* Category Grid */}
          <div className="w-full lg:w-[60%]">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
              {smallCategories.map((category) => (
                <article
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                  className="relative flex flex-col justify-end h-[140px] sm:h-[160px] md:h-[180px] bg-white rounded-lg overflow-hidden text-center uppercase cursor-pointer group transition hover:scale-[1.02]"
                >
                  <img
                    src={category.image || FALLBACK_IMAGE}
                    alt={category.title}
                    className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition"
                  />

                  <div className="relative bg-[#F5F5DC] p-2 sm:p-3 rounded-t-lg">
                    <h4 className="text-[10px] sm:text-xs md:text-sm font-semibold group-hover:text-teal-600 transition">
                      {category.title}
                    </h4>
                    <p className="text-[9px] sm:text-xs text-gray-600 mt-1 truncate overflow-hidden whitespace-nowrap">
                      {category.subtitle || "Explore"}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ShopByCategory;
